import React, {
    ForwardedRef,
    forwardRef,
    HTMLAttributes,
    useCallback,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react';

import { ListBox } from '../ListBox/ListBox';
import { ListBoxVirtualized } from '../ListBox/ListBoxVirtualized';
import { List } from '../List';
import { Drawer } from '../Drawer';
import { DrawerHeader } from '../Drawer/Header/Header';
import { useVStack } from '../../hooks/useStack';

import {
    defaultChildren,
    useComboboxProps,
    isComboboxStateVirtualized,
    isComboboxVirtualized,
    getOnOffAutoComplete,
} from './utils';
import { ComboboxProps } from './types';

const Combobox = forwardRef(function Combobox<ValueType extends string, SectionType extends string = ValueType>(
    props: ComboboxProps<ValueType, SectionType>,
    forwardedRef: ForwardedRef<HTMLInputElement>,
) {
    const {
        allowsCustomValue = true,
        children = defaultChildren,
        size = 'm',
        className,
        displaySelection = true,
        label,
        addonBeforeSection: propsAddonBeforeSection,
        pinnedSectionAfter: propsPinnedSectionAfter,
        emptySection: propsEmptySection,
        'data-testid': dataTestId = 'Combobox',
        popupWidth = 'max',
        manualControl,
    } = props;
    const stackClass = useVStack({ gap: '16' });

    const { LogProvider, state, listBoxRef, inputRef, popoverRef, inputProps, listBoxProps } = useComboboxProps(
        {
            ...props,
            menuTrigger: 'manual',
            shouldCloseOnBlur: false,
            allowsEmptyCollection: true,
            onOpenChange: isOpen => {
                if (isOpen) {
                    setOpenedValue(String(inputProps.value));
                }
                // Делаем поведение похожее на десктоп — при закрытии Combobox вызываем blur
                if (!isOpen) {
                    props.onBlur?.();
                }
                props.onOpenChange?.(isOpen);
            },
        },
        forwardedRef,
    );

    const [openedValue, setOpenedValue] = useState<string>(String(inputProps.value));
    const { revert, setInputValue, open } = state;
    const handleOpen = useCallback(() => open('first', 'manual'), [open]);

    const onCancelClick = useCallback(() => {
        if (allowsCustomValue) {
            setInputValue(openedValue);
        }
        revert();
    }, [allowsCustomValue, openedValue, revert, setInputValue]);

    const externalInputProps: Pick<
        HTMLAttributes<HTMLInputElement>,
        'role' | 'aria-expanded' | 'aria-controls' | 'aria-autocomplete' | 'aria-activedescendant'
    > = {
        role: inputProps.role,
        'aria-expanded': inputProps['aria-expanded'],
        'aria-controls': inputProps['aria-controls'],
        'aria-autocomplete': inputProps['aria-autocomplete'],
        'aria-activedescendant': inputProps['aria-activedescendant'],
    };

    const addonBeforeSection = useMemo(
        () =>
            typeof propsAddonBeforeSection === 'function'
                ? propsAddonBeforeSection(state.close)
                : propsAddonBeforeSection,
        [propsAddonBeforeSection, state.close],
    );

    const pinnedSectionAfter = useMemo(
        () =>
            typeof propsPinnedSectionAfter === 'function'
                ? propsPinnedSectionAfter(state.close)
                : propsPinnedSectionAfter,
        [propsPinnedSectionAfter, state.close],
    );

    const emptySection = useMemo(
        () => (typeof propsEmptySection === 'function' ? propsEmptySection(state.close) : propsEmptySection),
        [propsEmptySection, state.close],
    );

    useImperativeHandle(manualControl, () => {
        return {
            open: state.open,
            close: state.close,
            get isOpen() {
                return state.isOpen;
            },
        };
    }, [state.open, state.close, state.isOpen]);

    let drawerContent = null;

    if (state.collection.size === 0 && emptySection) {
        drawerContent = (
            <List size={'m'} key="empty" width={popupWidth}>
                {emptySection}
            </List>
        );
    } else if (isComboboxStateVirtualized(props, state) && isComboboxVirtualized(props)) {
        drawerContent = (
            <ListBoxVirtualized
                {...listBoxProps}
                selectionMode={'single'}
                ref={listBoxRef}
                state={state}
                size={'m'}
                items={props.items}
                data-testid={`${dataTestId}.listBox`}
                displaySelection={displaySelection}
                addonBeforeSection={addonBeforeSection}
                pinnedSection={props.pinnedSection}
                pinnedSectionAfter={pinnedSectionAfter}
            />
        );
    } else {
        drawerContent = (
            <ListBox
                {...listBoxProps}
                selectionMode={'single'}
                ref={listBoxRef}
                state={state}
                size={'m'}
                items={props.items}
                data-testid={`${dataTestId}.listBox`}
                displaySelection={displaySelection}
                addonBeforeSection={addonBeforeSection}
                pinnedSection={props.pinnedSection}
                pinnedSectionAfter={pinnedSectionAfter}
            />
        );
    }

    return (
        <LogProvider>
            <div className={className} data-testid={dataTestId}>
                {children({
                    ...props,
                    ...externalInputProps,
                    size,
                    isMobileTrigger: true,
                    onMobileTriggerPress: handleOpen,
                    onChange: state.setInputValue,
                    value: String(inputProps.value),
                    'data-testid': `${dataTestId}.Textinput`,
                })}
            </div>
            <Drawer
                ref={popoverRef}
                className={stackClass}
                isOpen={state.isOpen}
                onClose={state.close}
                header={
                    <DrawerHeader
                        onCancelClick={onCancelClick}
                        title={label}
                        onSaveClick={allowsCustomValue ? state.close : undefined}
                        data-testid={`${dataTestId}.Header`}
                    />
                }
                data-testid={`${dataTestId}.Drawer`}
                isFullscreen
            >
                {children({
                    ...props,
                    ...inputProps,
                    color: 'contrast',
                    autoFocus: true,
                    onChange: state.setInputValue,
                    onBlur: undefined,
                    size,
                    value: String(inputProps.value),
                    ref: inputRef,
                    inDrawer: true,
                    'data-testid': `${dataTestId}.DrawerTextinput`,
                    autoComplete: getOnOffAutoComplete(inputProps.autoComplete),
                    validationState: undefined,
                    spellCheck: String(inputProps.spellCheck) === 'true',
                })}
                {drawerContent}
            </Drawer>
        </LogProvider>
    );
});

export default Combobox;
